import { t as tr } from './i18n';
import { analyse, lineCost } from './analyse';
import { todayIso } from './format';
import { activeSupplements, lineValue, type Delivery } from './po';
import { orderValue } from './rental';
import { partOrder, rfqParts, splitCost } from './split';
import { rfqState } from './status';
import type { Rfq, SupplierRef } from './types';

/**
 * Ce s-a comandat pe un șantier: comenzile, banii, materialele.
 *
 * O comandă e o cerere la care ai ales furnizorul. Când are comandă de achiziție
 * emisă, valoarea și pozițiile vin din ea: e documentul trimis, cu ce s-a
 * negociat, și doar atunci intră în „Comandat”; fără ea, e „planificată”. Altfel, valoarea e cea trecută la alegere sau, lipsind și ea,
 * costul total al ofertei alese, marcat „estimată”; pozițiile sunt cele cerute,
 * cu prețurile ofertei — ce s-a cerut, nu neapărat ce s-a negociat.
 */

export interface LedgerLine {
  itemId: string;
  desc: string;
  qty: number;
  unit: string;
  /** prețul unitar din oferta aleasă, în lei fără TVA; null dacă n-a cotat poziția */
  unitPrice: number | null;
  /** valoarea plătită pe poziție (cu comanda minimă); null fără preț */
  value: number | null;
}

export interface LedgerOrder {
  rfqId: string;
  title: string;
  category: string;
  supplier: SupplierRef;
  /** ISO 8601, când s-a ales furnizorul */
  at: string;
  /** comanda de achiziție emisă, dacă există: de acolo vin valoarea și pozițiile */
  order: { id: string; number: string; deliveries: Delivery[] } | null;
  /** suplimentările către același furnizor: fără concurență, deci fără „diferență” */
  supplements: { id: string; number: string; value: number; deliveries: Delivery[] }[];
  /** chiria e încă pe șantier: valoarea crește în fiecare zi, până la retur */
  running: boolean;
  note: string;
  /** valoarea comenzii în lei fără TVA; null dacă nu se știe deloc */
  value: number | null;
  /** valoarea nu e trecută de om, e costul total al ofertei alese */
  estimated: boolean;
  /** costul total al ofertei alese, din comparație; null dacă oferta lipsește sau e incompletă */
  offerTotal: number | null;
  /**
   * față de cea mai bună altă ofertă completă: pozitiv = cât a ieșit mai ieftin,
   * negativ = cât a costat peste cea mai ieftină; null fără termen de comparație
   */
  gap: number | null;
  lines: LedgerLine[];
}

export interface LedgerMaterial {
  desc: string;
  unit: string;
  qty: number;
  /** valoarea pozițiilor cu preț */
  value: number;
  /** prețul mediu ponderat pe unitate; null dacă nicio poziție nu are preț */
  avgPrice: number | null;
  /** în câte comenzi apare */
  orders: number;
}

export interface LedgerShare {
  label: string;
  total: number;
}

export interface SiteLedger {
  orders: LedgerOrder[];
  /** suma comenzilor de achiziție emise: ce s-a comandat de fapt */
  ordered: number;
  /** câte comenzi de achiziție emise sunt */
  orderedCount: number;
  /**
   * furnizor ales, comanda încă neemisă: valoarea trecută la alegere sau, fără
   * ea, costul ofertei alese. Hotărât, dar necomandat — nu intră în `ordered`.
   */
  planned: number;
  /** câte alegeri fără comandă sunt */
  plannedCount: number;
  /** câte din cele planificate au valoarea estimată după ofertă */
  estimatedCount: number;
  /** câte din cele planificate nu au nicio valoare */
  unknownCount: number;
  /** cererile deschise, încă fără furnizor ales */
  openCount: number;
  /** cel mai mic cost total pe cererile deschise care au deja o ofertă completă */
  inProgress: number;
  /** câte cereri deschise intră în `inProgress` */
  inProgressCount: number;
  /** suma diferențelor pozitive: cât au ieșit mai ieftin decât următoarea ofertă */
  gained: number;
  /** la câte comenzi s-a putut calcula diferența pozitivă */
  gainedCount: number;
  materials: LedgerMaterial[];
  byCategory: LedgerShare[];
  bySupplier: LedgerShare[];
}

/** Cheia unui material: aceeași denumire și unitate, oricum ar fi scrisă literă cu literă. */
const materialKey = (desc: string, unit: string) =>
  `${desc.trim().toLocaleLowerCase('ro').replace(/\s+/g, ' ')}|${unit.trim().toLocaleLowerCase('ro')}`;

function shares(map: Map<string, number>): LedgerShare[] {
  return [...map.entries()]
    .map(([label, total]) => ({ label, total }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'ro'));
}

/**
 * Registrul unui șantier, din cererile lui întregi. Cele anulate nu intră.
 * `today` ('AAAA-LL-ZZ') socotește chiria încă nereturnată.
 */
export function siteLedger(rfqs: Rfq[], today: string = todayIso()): SiteLedger {
  const orders: LedgerOrder[] = [];
  let openCount = 0;
  let inProgress = 0;
  let inProgressCount = 0;

  for (const rfq of rfqs) {
    const state = rfqState(rfq);
    if (state === 'canceled') continue;
    const a = analyse(rfq);

    if (!rfq.chosen) {
      if (state !== 'open') continue;
      openCount += 1;
      if (a.full.length) {
        inProgress += Math.min(...a.full.map((c) => c.total));
        inProgressCount += 1;
      }
      continue;
    }

    const chosen = rfq.chosen;
    // Comanda împărțită: un rând pe fiecare furnizor, cu pozițiile și comanda lui.
    const split = !!chosen.split?.length;
    const parts = rfqParts(rfq);
    const splitTotal = split
      ? splitCost(
          rfq,
          parts.map((p) => ({ supplierId: p.supplier.id, itemIds: p.itemIds })),
        ).total
      : null;

    parts.forEach((part, index) => {
      const send = rfq.sends.find((s) => s.supplier.id === part.supplier.id);
      const offer = send?.offer ?? null;
      const col = a.full.find((c) => c.supplier.id === part.supplier.id);
      const offerTotal = split
        ? splitCost(rfq, [{ supplierId: part.supplier.id, itemIds: part.itemIds }]).total
        : col
          ? col.total
          : null;
      const po = partOrder(rfq, part.supplier.id, 'issued');
      const sups = activeSupplements(rfq).filter((x) => x.supplier.id === part.supplier.id);
      const fx = po?.data.currency === 'EUR' ? (po.data.fxRate ?? 1) : 1;
      // valoarea reală: la chirie livrată, pe zilele de pe șantier, nu pe cele cerute
      const poValue = po ? orderValue(po, today) : null;
      const supValues = sups.map((x) => ({ x, v: orderValue(x, today) }));
      const supTotal = supValues.reduce((n, { v }) => n + v.lei, 0);
      // Împărțită: diferența e a întregii împărțiri față de cea mai bună ofertă
      // completă, trecută o singură dată, pe partea cea mai mare.
      const fulls = a.full.map((c) => c.total);
      const others = a.full.filter((c) => c !== col).map((c) => c.total);
      const gap = split
        ? index === 0 && splitTotal != null && fulls.length
          ? Math.min(...fulls) - splitTotal
          : null
        : col && others.length
          ? Math.min(...others) - col.total
          : null;
      // Valoarea trecută la alegere e a întregii cereri: la o parte, estimarea din ofertă.
      const typed = split ? null : chosen.total;

      orders.push({
        rfqId: rfq.id,
        title: rfq.title,
        category: rfq.category,
        supplier: part.supplier,
        at: chosen.at,
        note: chosen.note,
        order: po ? { id: po.id, number: po.number, deliveries: po.deliveries ?? [] } : null,
        supplements: supValues.map(({ x, v }) => ({
          id: x.id,
          number: x.number,
          value: v.lei,
          deliveries: x.deliveries ?? [],
        })),
        running: !!poValue?.running || supValues.some(({ v }) => v.running),
        value: po ? poValue!.lei + supTotal : (typed ?? offerTotal),
        estimated: !po && typed == null && offerTotal != null,
        offerTotal,
        gap,
        // Pe comandă, sumele sunt în moneda ei; registrul le ține în lei.
        lines: po
          ? [po, ...sups].flatMap((o) => {
              const ofx = o.data.currency === 'EUR' ? (o.data.fxRate ?? 1) : fx;
              return o.data.lines.map((l, i) => ({
                itemId: `${o.id}:${i}`,
                desc: l.desc,
                qty: l.qty,
                unit: l.unit,
                unitPrice: l.unitPrice * ofx,
                value: lineValue(l) * ofx,
              }));
            })
          : rfq.items
              .filter((it) => part.itemIds.includes(it.id))
              .map((it) => ({
                itemId: it.id,
                desc: it.desc,
                qty: it.qty,
                unit: it.unit,
                unitPrice: offer?.prices[it.id] ?? null,
                value: offer ? lineCost(offer, it) : null,
              })),
      });
    });
  }

  orders.sort((x, y) => (x.at < y.at ? 1 : x.at > y.at ? -1 : 0));

  const materials = new Map<string, LedgerMaterial & { priced: number }>();
  const byCategory = new Map<string, number>();
  const bySupplier = new Map<string, number>();
  for (const o of orders) {
    if (o.value != null) {
      byCategory.set(o.category, (byCategory.get(o.category) ?? 0) + o.value);
      bySupplier.set(o.supplier.name, (bySupplier.get(o.supplier.name) ?? 0) + o.value);
    }
    for (const l of o.lines) {
      const key = materialKey(l.desc, l.unit);
      let m = materials.get(key);
      if (!m) {
        m = {
          desc: l.desc.trim().replace(/\s+/g, ' '), // scrierea din comanda cea mai nouă
          unit: l.unit,
          qty: 0,
          value: 0,
          avgPrice: null,
          orders: 0,
          priced: 0,
        };
        materials.set(key, m);
      }
      m.qty += l.qty;
      m.orders += 1;
      if (l.value != null) {
        m.value += l.value;
        m.priced += l.qty;
      }
    }
  }

  const gains = orders.filter((o) => o.gap != null && o.gap > 0);
  const withPo = orders.filter((o) => o.order);
  const withoutPo = orders.filter((o) => !o.order);
  return {
    orders,
    ordered: withPo.reduce((n, o) => n + (o.value ?? 0), 0),
    orderedCount: withPo.length,
    planned: withoutPo.reduce((n, o) => n + (o.value ?? 0), 0),
    plannedCount: withoutPo.length,
    estimatedCount: orders.filter((o) => o.estimated).length,
    unknownCount: orders.filter((o) => o.value == null).length,
    openCount,
    inProgress,
    inProgressCount,
    gained: gains.reduce((n, o) => n + o.gap!, 0),
    gainedCount: gains.length,
    materials: [...materials.values()]
      .map(({ priced, ...m }) => ({ ...m, avgPrice: priced ? m.value / priced : null }))
      .sort((x, y) => y.value - x.value || x.desc.localeCompare(y.desc, 'ro')),
    byCategory: shares(byCategory),
    bySupplier: shares(bySupplier),
  };
}

/** Materialele ca TSV, pentru lipit în Excel. */
export function materialsTsv(materials: LedgerMaterial[]): string {
  const num = (n: number) => String(Math.round(n * 100) / 100).replace('.', ',');
  return [
    [
      tr('Material'),
      tr('Cantitate'),
      'UM',
      tr('Preț mediu (lei fără TVA)'),
      tr('Valoare (lei fără TVA)'),
      tr('Comenzi'),
    ],
    ...materials.map((m) => [
      m.desc,
      num(m.qty),
      m.unit,
      m.avgPrice == null ? '' : num(m.avgPrice),
      num(m.value),
      String(m.orders),
    ]),
  ]
    .map((r) => r.join('\t'))
    .join('\n');
}
